import React, { useEffect, useRef, useState } from "react";
import { useParams, useOutletContext, useNavigate } from 'react-router-dom';
import * as http from "../../../../utils/http";
import SubscriptionCancellationSheet from "../CarePlus/SubscriptionCancellationSheet/SubscriptionCancellationSheet";
import CarePlusCancelModal from "../CarePlus/CarePlusCancelModal/CarePlusCancelModal";
import InfoCell from "../../../../components/InfoCell/InfoCell";
import EditableInfoCard from "../EditableInfoCard/EditableInfoCard";
import "./MyAccountEditSubscriptionPanel.css";
import * as translator from '../../../../utils/translator';
import SubmitButton from "../../../../components/SubmitButton/SubmitButton";
import { RiInformationLine } from "react-icons/ri";
import { MainTracker } from '../../../../PixelTrackers/MainTracker';
import Significance from "../../../Questionnaire/components/Significance/Significance";

interface MyAccountEditSubscriptionPanelProps {
  showCancelModal?: boolean
  orderSubscriptionData: any;
}

/** The Care+ cancellation flow state (GET …/cancellation-flow), as the Care+ order page reads it. */
interface CancellationFlowState {
  eligible: boolean;
  request: { id: number; type: 'cancel' | 'one_time_order'; reason: string; cancelAt: string } | null;
  offer: { available: boolean };
  progress?: { week: number };
  pause?: { minDate: string; maxDate: string } | null;
  paused?: boolean;
}

const MyAccountEditSubscriptionPanel: React.FC<MyAccountEditSubscriptionPanelProps> = ({showCancelModal, orderSubscriptionData}) => {
  const {language, country, setCurrentGlobalModalName, currentGlobalModalName, setStickyNotificationDetails, setRefreshData} = useOutletContext<any>();
  const {order_hash_id} = useParams<{  order_hash_id: string | undefined }>(); //the hashID from url
  const navigate = useNavigate();
  // A Care+ plan is cancelled only through its own pop-ups: the cancellation flow sheet (pause,
  // one-time order, cancellation request, retention offer) when the customer is eligible for it,
  // otherwise the Care+ cancel modal. The legacy cancel modal and its endpoint know none of the Care+
  // rules, so they are never used for a Care+ plan.
  const isCarePlus = Boolean(orderSubscriptionData?.isCarePlus);
  // nothing to cancel before the first payment activates a Care+ plan
  const canCancel = !isCarePlus || orderSubscriptionData?.status === 'active';
  const orderPagePath = `/${country}/account/my-orders/${order_hash_id}/details`;
  const [carePlusFlow, setCarePlusFlow] = useState<CancellationFlowState | null>(null);
  const [carePlusPopup, setCarePlusPopup] = useState<'sheet' | 'cancel' | null>(null);
  const openingCarePlus = useRef(false);
  const openCarePlusCancel = () => {
    if (openingCarePlus.current) return;
    openingCarePlus.current = true;
    http.getFromServer(`${country}/account/care-plus-subscription/${order_hash_id}/cancellation-flow`, [http.MIDDLEWARES.CUSTOMER_ID])
      .then((res: any) => {
        const flow: CancellationFlowState | null = res?.data ?? null;
        // a cancellation is already requested: the order page shows it, with the option to withdraw
        if (flow?.request) { navigate(orderPagePath); return; }
        setCarePlusFlow(flow);
        setCarePlusPopup(flow?.eligible ? 'sheet' : 'cancel');
      })
      // without the flow state the order page still has every Care+ option
      .catch(() => navigate(orderPagePath))
      .finally(() => { openingCarePlus.current = false; });
  };
  const openCancel = () => {
    if (isCarePlus) {
      openCarePlusCancel();
      return;
    }
    setCurrentGlobalModalName({type: "CANCEL_SUBSCRIPTION", subscriptionData: orderSubscriptionData});
  };
  // ?showCancelModal=true on a Care+ plan opens the Care+ pop-up (once), never the legacy modal
  useEffect(() => {
    if (showCancelModal && isCarePlus && canCancel && order_hash_id) openCarePlusCancel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCancelModal, isCarePlus, canCancel, order_hash_id]);
  if(order_hash_id){
    if (showCancelModal && currentGlobalModalName === null && !isCarePlus) {
      setCurrentGlobalModalName({
        type: "CANCEL_SUBSCRIPTION",
        subscriptionData: orderSubscriptionData
      })
    }
    const subscriptionData = orderSubscriptionData
    const discountValue = (subscriptionData.regularPrice - subscriptionData.subscriptionPrice).toFixed(2) + ' ' + subscriptionData.currencySign
    const nextPayment:any = new Date(subscriptionData.rawNextBilling);
    const nextDelivery:any = new Date(subscriptionData.rawNextDelivery); 
    const deliverySkipped:any = new Date(subscriptionData.rawDeliverySkipped);  

    const today:any = new Date();

    const prePayment = nextPayment > today ? true : false //allow skipping only if next payment is after today

    let timeDifference
    if(deliverySkipped && prePayment){
      timeDifference = deliverySkipped - today; // in miliseconds
    }else{
      timeDifference = nextDelivery - today
    }
    const daysUntilNextSkip = Math.ceil(timeDifference / (1000 * 60 * 60 * 24)); //convert to days

    return <>
      <div className="edit-subscription-panel">
        {
          subscriptionData.isQuestionnaireExpired &&
          <Significance dangerSignificancesRefsDispatch={null} parentID={null} showIcon level={"danger"} fontWeight={400} fontSize={'16px'} text={`Ihre medizinischen Daten müssen aktualisiert werden, damit die nächste Bestellung Ihres Abonnements erstellt werden kann. Bitte {klicken Sie hier:onclick="window.location.href = window.location.pathname + '#renew_questionnaire_banner'; location.reload()"}, um sie zu überprüfen und gegebenenfalls zu ändern`}/>
        }
        <div className="info-cells-row">
          <InfoCell title={translator.translate(language, "my_account", 'SubscriptionOrders/infoCells/dosage')} value={subscriptionData.infoCells.dosage} />
          <InfoCell title={translator.translate(language, "my_account", 'SubscriptionOrders/infoCells/pillCount')} value={subscriptionData.infoCells.pillQuantity} />
          <InfoCell title={translator.translate(language, "my_account", 'SubscriptionOrders/infoCells/plan')} value={subscriptionData.infoCells.plan} />
        </div>
          { ((!subscriptionData.deliverySkipped || (today > deliverySkipped)) && prePayment) ? (
          <div className="skip-subscription-container">
            <div className="skip-subscription-text-icon-container">
              <RiInformationLine className="skip-subscription-info-icon"/>
              <p className="skip-subscription-text">{translator.translate(language, "my_account", 'SubscriptionOrders/skip_subscription_disclaimer').replace(":discountValue", discountValue)}</p>
            </div>
            <SubmitButton 
              loading={false}
              onSubmit={()=>{
                setCurrentGlobalModalName({type:"SKIP_SUBSCRIPTION", subscriptionData: orderSubscriptionData, data:{hashID:order_hash_id, from:'mySubscriptions'}})
                MainTracker.track('pv', 'View Pop Up', {'Pop Up Name': 'Skip my upcoming order from my account'})
              }
            }
              id={"subscription-delivery-address-submit"}
              fontsize={"14px"}
              padding={"0px 30px"}
              height={"45px"}
              placeholder={translator.translate(language, "my_account", 'SubscriptionOrders/skip_subscription_button')}
              isEnabledState />
          </div>
          ):(
            <div className="skip-subscription-container skip-disabled">
            <div className="skip-subscription-text-icon-container skip-disabled">
              <RiInformationLine className="skip-subscription-info-icon skip-disabled"/>
            <div className="skip-subscription-text-container">
              {
                (!prePayment && !subscriptionData.rawPausedAt) ? 
                  (
                    <>
                      <p className="skip-subscription-title skip-disabled">{translator.translate(language, "my_account", 'SubscriptionOrders/skip_subscription_title').replace(":days", daysUntilNextSkip.toString())}</p>
                      <p className="skip-subscription-text skip-disabled">{translator.translate(language, "my_account", 'SubscriptionOrders/skip_subscription_text')}</p>
                    </>
                  )
                :
                  (!prePayment && subscriptionData.rawPausedAt) ? 
                      (
                        <>
                          <p className="skip-subscription-title skip-disabled">{translator.translate(language, "my_account", 'SubscriptionOrders/skip_subscription_text_3')}</p>
                        </> 
                      )
                    :
                      (
                        <>
                          <p className="skip-subscription-title skip-disabled">{translator.translate(language, "my_account", 'SubscriptionOrders/skip_subscription_title_2').replace(":daysUntilNextSkip", daysUntilNextSkip.toString())}</p>
                          <p className="skip-subscription-text skip-disabled">{translator.translate(language, "my_account", 'SubscriptionOrders/skip_subscription_text_2').replace(":subscriptionData.deliverySkipped", subscriptionData.deliverySkipped)}</p>
                        </>
                      )
              }
            </div>
            </div>
            <SubmitButton 
              loading={false}
              id={"subscription-skip-submit-disabled"}
              fontsize={"14px"}
              padding={"0px 30px"}
              height={"45px"}
              placeholder={translator.translate(language, "my_account", 'SubscriptionOrders/skip_subscription_button')}
              isEnabledState />
          </div>
          )}

          <EditableInfoCard orderSubscriptionData={orderSubscriptionData} editSection={"delivery-address"} title={translator.translate(language, "my_account", 'SubscriptionOrders/infoCards/address')} value={subscriptionData.address} language={language}/>
          <EditableInfoCard orderSubscriptionData={orderSubscriptionData} editSection={"treatment-questionnaire"} title={translator.translate(language, "my_account", 'SubscriptionOrders/infoCards/treatmentQuestionnaire')} value={subscriptionData.catalog_title} language={language}/>

          {canCancel && (
            <span onClick={openCancel} className="cancel-subscription">{translator.translate(language, "my_account", 'SubscriptionOrders/cancel')}</span>
          )}
        </div>
        {carePlusPopup === 'sheet' && carePlusFlow && (
          <SubscriptionCancellationSheet
            subscriptionID={orderSubscriptionData.subscriptionID}
            country={country}
            language={language}
            offerAvailable={Boolean(carePlusFlow.offer?.available)}
            treatmentWeek={carePlusFlow.progress?.week ?? 1}
            pauseWindow={carePlusFlow.pause ?? null}
            isPaused={Boolean(carePlusFlow.paused)}
            onClose={() => setCarePlusPopup(null)}
            // a request was created or the offer accepted: the order page shows the new state
            onChanged={() => { setCarePlusPopup(null); setRefreshData?.((p: boolean) => !p); navigate(orderPagePath); }}
            // the pause modal lives on the order page
            onOpenPause={() => navigate(orderPagePath)}
            setStickyNotificationDetails={setStickyNotificationDetails}
          />
        )}
        {carePlusPopup === 'cancel' && (
          <CarePlusCancelModal
            subscriptionID={orderSubscriptionData.subscriptionID}
            subscriptionHashID={order_hash_id}
            planType={orderSubscriptionData.planType}
            isMonthlyPlan={Boolean(orderSubscriptionData.isMonthlyPlan)}
            language={language}
            country={country}
            isPaused={orderSubscriptionData.status === 'paused'}
            onClose={() => setCarePlusPopup(null)}
            onSuccess={() => { setCarePlusPopup(null); setRefreshData?.((p: boolean) => !p); navigate(`/${country}/account/my-orders`); }}
            onOpenPause={() => navigate(orderPagePath)}
            setStickyNotificationDetails={setStickyNotificationDetails}
          />
        )}
    </>
  }else{
    return(<>400</>)
  }
};

export default MyAccountEditSubscriptionPanel;
